/**
 * Types and constants for the professional payments feature (Easypay + Magnifinance).
 *
 * These mirror the contracts already in production on the payments API
 * (REACT_APP_PAYMENTS_API_URL). The API is fixed — treat the casing below as
 * specification, not as something to normalise. Notably the payloads mix
 * snake_case and camelCase, and the request/response are asymmetric
 * (we send `customer.fiscal_number`, we receive `customer.nif`).
 */

/** Easypay KYC review state. Written by the backend only — the client just reads it. */
export const EASYPAY_STATES = [
  "NOT_REGISTERED",
  "WAITING_IDS",
  "WAITING_DOCUMENTS_UPLOAD",
  "REVIEWING",
  "DONE",
  "ERROR"
] as const;
export type EasypayState = typeof EASYPAY_STATES[number];

/**
 * Legal form of the billing entity. Determines the required KYC document
 * matrix and cannot be changed after the configuration is created.
 */
export const COMPANY_TYPES = [
  "INDIVIDUAL_NAME_BUSINESS_MAN",
  "UNIPERSONAL_COMPANY",
  "MULTIPERSONAL_COMPANY"
] as const;
export type CompanyType = typeof COMPANY_TYPES[number];

export const PAYMENT_DOCUMENT_TYPES = [
  "CITIZEN_CARD_FRONT",
  "CITIZEN_CARD_BACK",
  "PROFESSIONAL_CARD_FRONT",
  "PROFESSIONAL_CARD_BACK",
  "INTERNATIONAL_BANK_ACCOUNT_NUMBER",
  "ADDRESS_VALIDATION",
  "COMPANY_VALIDATION",
  "OWNERSHIP_AND_BENEFICIARIES",
  "PERMANENT_REGISTRY_CERTIFICATE"
] as const;
export type PaymentDocumentType = typeof PAYMENT_DOCUMENT_TYPES[number];

/** Transaction lifecycle states returned by GET /transaction. */
export const PAYMENT_STATUSES = [
  "pending",
  "payment_pending",
  "payment_error",
  "paid",
  "expired"
] as const;
export type PaymentStatus = typeof PAYMENT_STATUSES[number];

/** Repeatable KYC document groups — each entry needs its own set of documents. */
export const DOCUMENT_GROUP_KEYS = {
  BENEFICIARIES: "BENEFICIARIES",
  LEGAL_REPRESENTATION: "LEGAL_REPRESENTATION"
} as const;
export type DocumentGroupKey = typeof DOCUMENT_GROUP_KEYS[keyof typeof DOCUMENT_GROUP_KEYS];

/** idGroup used by documents that are not part of a repeatable group. */
export const SINGLE_DOCUMENT_GROUP_ID = "0";

/** Portuguese "final consumer" tax number, sent when the patient has no NIF. */
export const FINAL_CONSUMER_FISCAL_NUMBER = "999999990";

/** Maximum unit cost accepted on a payment line. */
export const MAX_LINE_UNIT_COST = 15001.0;

export interface PaymentDocument {
  id: string;
  documentType: PaymentDocumentType;
  /** SINGLE_DOCUMENT_GROUP_ID for single documents, `${groupKey}_${index}` for grouped ones. */
  idGroup: string;
  stored?: boolean;
}

export interface PaymentConfiguration {
  id: string;
  skuId: string;
  paymentId: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  fiscalId: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  iban: string;
  email: string;
  /** Fraction, not a percentage: 0.05 means 5%. */
  commission: number;
  easypayCompanyType: CompanyType;
  easypayState: EasypayState;
  /** 36 char UUID, written by the backend once Easypay approves the account. */
  easypayAccountId?: string;
  easypayDocuments?: PaymentDocument[];
  /** The password is never returned by the API, only the username. */
  AT?: { username: string; password?: string };
  documentsReady?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePaymentConfigurationInput {
  firstName: string;
  lastName: string;
  fiscalId: string;
  paymentId: string;
  skuId: string;
  address: string;
  postalCode: string;
  city: string;
  organizationName: string;
  /** Dial code, a space, then the number — e.g. "+351 912345678". */
  phone: string;
  iban: string;
  easypayCompanyType: CompanyType;
  email: string;
}

export interface TransactionLine {
  code: string;
  description: string;
  /** Unit price, NOT multiplied by the quantity. */
  value: number;
  quantity: number;
}

export interface RequestPaymentInput {
  configId: string;
  /** The professional's paymentId. */
  issuer: string;
  /** The patient's id. */
  receiver: string;
  taxFree: true;
  /** The professional's planName / skuId. */
  planId: string;
  customer: {
    name: string;
    email: string;
    fiscal_number: string;
    phone_indicative: string;
    phone: string;
  };
  order: { value: number; items: TransactionLine[] };
}

export interface Transaction {
  id?: string;
  status: PaymentStatus;
  value: number;
  items: TransactionLine[];
  customer: { name: string; email: string; nif: string };
  created_at?: string;
  paid_at?: string;
  /** The receipt link is called urlReceipt, not receiptUrl. */
  urlReceipt?: string;
}

export interface PaymentsPage {
  total: number;
  items: Transaction[];
  per_page: number;
}

/** Response of GET /api/professionals/tax_id/{nif} on the web API. */
export interface TaxIdRecord {
  nif: string;
  title: string;
  address: string;
  pc4: string;
  pc3: string;
  city: string;
  activity: string;
  status: string;
  cae: string;
  seo_url: string;
  contacts?: { email: string; phone: string; website: string; fax: string };
  place?: { address: string; pc4: string; pc3: string; city: string };
}

/**
 * A payment configuration can only be used to charge a patient once Easypay has
 * created the account AND the tax authority credentials are stored.
 */
export function canConfigurationCharge(config: PaymentConfiguration): boolean {
  return Boolean(config.easypayAccountId && config.AT?.username);
}

/**
 * Whether the configuration is shown as ready in the list. This is deliberately
 * a slightly different check from canConfigurationCharge: it is the visual
 * indicator, while the former is the functional gate for charging.
 */
export function isConfigurationReady(config: PaymentConfiguration): boolean {
  return Boolean(config.AT?.username && config.easypayState === "DONE");
}
